import type { RecentActionsMap } from '../../contexts/RecentActionsContext';
import type { PatientListItemType } from '../../providers/patient/PatientsListProvider';
import { hasActionRequired, isInactiveStatus } from '../../utils/constants';

export type PatientsSortBy = 'name' | 'treatmentStatus' | 'actionRequired' | 'nextAppointment';
export type SortDir = 'asc' | 'desc';

export const isInactivePatient = (patient: PatientListItemType): boolean => {
    return isInactiveStatus(patient.treatmentStatus);
};

const sortByName = (a: PatientListItemType, b: PatientListItemType, sortDir: SortDir) => {
    const nameA = ((a.name as string | null) || '').trim();
    const nameB = ((b.name as string | null) || '').trim();
    const cmp = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
    return cmp * (sortDir === 'asc' ? 1 : -1);
};

const sortByTreatmentStatus = (
    a: PatientListItemType,
    b: PatientListItemType,
    sortDir: SortDir
) => {
    const treatmentStatusA = (a.treatmentStatus as string | null) || '';
    const treatmentStatusB = (b.treatmentStatus as string | null) || '';
    const cmp =
        treatmentStatusA.localeCompare(treatmentStatusB, undefined, { sensitivity: 'base' }) *
        (sortDir === 'asc' ? 1 : -1);
    if (cmp !== 0) return cmp;
    return sortByName(a, b, sortDir);
};

/**
 * The action a patient's row sorts by: their real, current `requiredAction`
 * when they have one, otherwise the action they recently resolved (while
 * RecentActionsContext's global sliding window is still open) - so
 * completing an action doesn't reshuffle the row out from under the user
 * while staff keep working the queue.
 */
const getEffectiveAction = (
    patient: PatientListItemType,
    recentActions: RecentActionsMap
): string => {
    const actual = (patient.requiredAction as string | null) || '';
    if (hasActionRequired(actual)) return actual;
    const patientId = patient.id;
    const recent = patientId ? recentActions[patientId] : undefined;
    return recent ? recent.actionLabel : actual;
};

const sortByActionRequired = (
    a: PatientListItemType,
    b: PatientListItemType,
    sortDir: SortDir,
    recentActions: RecentActionsMap
) => {
    const sortDirNumber = sortDir === 'asc' ? 1 : -1;
    const actionA = getEffectiveAction(a, recentActions);
    const actionB = getEffectiveAction(b, recentActions);
    const aIsNone = !hasActionRequired(actionA);
    const bIsNone = !hasActionRequired(actionB);
    if (aIsNone && !bIsNone) return 1 * sortDirNumber;
    if (!aIsNone && bIsNone) return -1 * sortDirNumber;
    const cmp = actionA.localeCompare(actionB, undefined, { sensitivity: 'base' }) * sortDirNumber;
    if (cmp !== 0) return cmp;
    return sortByName(a, b, sortDir);
};

/**
 * Sorts by the same soonest-upcoming-appointment timestamp the NEXT
 * APPOINTMENT column displays (see NextAppointmentCell / PatientTable's
 * nextAppointmentByPatientId / nextAppointmentTimestampByPatientId), so sort
 * order and display can never disagree. Patients without a resolved
 * timestamp - no upcoming appointment, or the appointments query hasn't
 * loaded yet - always sort last regardless of direction, the same "always
 * last" treatment inactive patients get in `sortPatients`; equal timestamps
 * (including two patients both missing one) tiebreak by name.
 */
const sortByNextAppointment = (
    a: PatientListItemType,
    b: PatientListItemType,
    sortDir: SortDir,
    nextAppointmentTimestampByPatientId: Map<string, number>
) => {
    const sortDirNumber = sortDir === 'asc' ? 1 : -1;
    const timestampA = nextAppointmentTimestampByPatientId.get(a.id);
    const timestampB = nextAppointmentTimestampByPatientId.get(b.id);
    if (timestampA === undefined && timestampB === undefined) {
        return sortByName(a, b, sortDir);
    }
    if (timestampA === undefined) return 1;
    if (timestampB === undefined) return -1;
    if (timestampA === timestampB) return sortByName(a, b, sortDir);
    return (timestampA - timestampB) * sortDirNumber;
};

/**
 * Master comparator for the patients table: inactive patients always sort
 * last regardless of column/direction, then the active `sortType`/`sortDir`
 * decides among the rest. Kept as one function (rather than
 * `Array.prototype.sort`'s comparator inlined at the call site) so
 * `PatientTable` and any future consumer share the exact same ordering.
 */
export function sortPatients(
    a: PatientListItemType,
    b: PatientListItemType,
    sortType: PatientsSortBy,
    sortDir: SortDir,
    recentActions: RecentActionsMap,
    nextAppointmentTimestampByPatientId: Map<string, number>
): number {
    const aInactive = isInactivePatient(a);
    const bInactive = isInactivePatient(b);
    if (aInactive !== bInactive) return aInactive ? 1 : -1;

    switch (sortType) {
        case 'name':
            return sortByName(a, b, sortDir);
        case 'treatmentStatus':
            return sortByTreatmentStatus(a, b, sortDir);
        case 'actionRequired':
            return sortByActionRequired(a, b, sortDir, recentActions);
        case 'nextAppointment':
            return sortByNextAppointment(a, b, sortDir, nextAppointmentTimestampByPatientId);
    }
}
